/**
 * 🚀 useBookingMutations - Легковесный хук ТОЛЬКО для мутаций бронирований
 * 
 * Выделен из useBookingCore для устранения паразитной загрузки ВСЕХ записей.
 * НЕ содержит useState, useEffect, fetchBookings.
 * Только мутации + инвалидация кэша + broadcast (АК-47).
 */

import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import { useRealtime } from '@/contexts/RealtimeContext';
import { createComponentLogger } from '@/lib/logger/ProductionLogger';
import { useQueryClient } from '@tanstack/react-query';

const logger = createComponentLogger('useBookingMutations');

export const useBookingMutations = () => {
  const { currentHotel } = useOptimizedHotel();
  const { broadcastFinancialChange, broadcastBookingChange } = useRealtime();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const addPayment = async (bookingId: string, paymentData: {
    amount: number;
    payment_method: string;
    payment_type: string;
    transaction_id?: string;
    notes?: string;
  }) => {
    try {
      logger.info('Adding payment', { bookingId, paymentData });

      const { data, error } = await supabase.rpc('add_booking_payment_to_core', {
        p_booking_id: bookingId,
        p_amount: paymentData.amount,
        p_payment_method: paymentData.payment_method,
        p_payment_type: paymentData.payment_type,
        p_notes: paymentData.notes || '',
        p_transaction_id: paymentData.transaction_id || '',
        p_changed_by: null
      });

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['optimized-bookings', currentHotel?.id], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['grid-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });

      broadcastFinancialChange('payment_added', bookingId);

      toast({
        title: "Успешно",
        description: `Платеж на сумму ${paymentData.amount} ₽ добавлен`,
      });

      return data;
    } catch (error: any) {
      logger.error('Error adding payment', { error });
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось добавить платеж",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updatePayment = async (paymentId: string, updates: {
    amount?: number;
    notes?: string;
  }) => {
    try {
      logger.info('Updating payment', { paymentId, updates });

      const { data: paymentRecord, error: fetchError } = await supabase
        .from('booking_payments')
        .select('booking_id')
        .eq('id', paymentId)
        .single();

      if (fetchError) throw fetchError;

      const { data, error } = await supabase
        .from('booking_payments')
        .update(updates)
        .eq('id', paymentId)
        .select()
        .single();

      if (error) throw error;

      await supabase.rpc('recalc_booking_paid_amount', { p_booking_id: paymentRecord.booking_id });

      queryClient.invalidateQueries({ queryKey: ['optimized-bookings', currentHotel?.id], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['grid-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });

      broadcastFinancialChange('payment_updated', paymentId);

      toast({
        title: "Успешно",
        description: "Платеж обновлен",
      });

      return data;
    } catch (error: any) {
      logger.error('Error updating payment', { error });
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось обновить платеж",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deletePayment = async (paymentId: string) => {
    try {
      logger.info('Deleting payment', { paymentId });

      const { data: paymentRecord, error: fetchError } = await supabase
        .from('booking_payments')
        .select('booking_id')
        .eq('id', paymentId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('booking_payments')
        .delete()
        .eq('id', paymentId);

      if (error) throw error;

      await supabase.rpc('recalc_booking_paid_amount', { p_booking_id: paymentRecord.booking_id });

      queryClient.invalidateQueries({ queryKey: ['optimized-bookings', currentHotel?.id], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['grid-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });

      broadcastFinancialChange('payment_deleted', paymentId);

      toast({
        title: "Успешно",
        description: "Платеж удален",
      });

      return true;
    } catch (error: any) {
      logger.error('Error deleting payment', { error });
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось удалить платеж",
        variant: "destructive",
      });
      throw error;
    }
  };

  const addService = async (bookingId: string, serviceData: {
    service_name: string;
    quantity: number;
    unit_price: number;
    total_price: number;
    service_date?: string;
    notes?: string;
  }) => {
    try {
      logger.info('Adding service', { bookingId, serviceData });

      const { data, error } = await supabase
        .from('booking_services')
        .insert({
          booking_id: bookingId,
          service_name: serviceData.service_name,
          quantity: serviceData.quantity,
          unit_price: serviceData.unit_price,
          total_price: serviceData.total_price,
          service_date: serviceData.service_date,
          notes: serviceData.notes,
          service_type: 'additional',
          status: 'active'
        })
        .select()
        .single();

      if (error) throw error;

      queryClient.invalidateQueries({
        queryKey: ['optimized-bookings', currentHotel?.id],
        refetchType: 'all'
      });

      toast({
        title: "Успешно",
        description: `Услуга "${serviceData.service_name}" добавлена`,
      });

      return data;
    } catch (error: any) {
      logger.error('Error adding service', { error });
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось добавить услугу",
        variant: "destructive",
      });
      throw error;
    }
  };

  const addGuest = async (bookingId: string, guestData: {
    guest_id: string;
    is_main_guest: boolean;
    guest_type: string;
    arrival_date?: string;
    departure_date?: string;
  }) => {
    try {
      logger.info('Adding guest', { bookingId, guestData });

      const { data, error } = await supabase
        .from('booking_guests')
        .insert({
          booking_id: bookingId,
          guest_id: guestData.guest_id,
          is_main_guest: guestData.is_main_guest,
          guest_type: guestData.guest_type,
          arrival_date: guestData.arrival_date,
          departure_date: guestData.departure_date
        })
        .select()
        .single();

      if (error) throw error;

      queryClient.invalidateQueries({
        queryKey: ['optimized-bookings', currentHotel?.id],
        refetchType: 'active'
      });

      toast({
        title: "Успешно",
        description: "Гость добавлен к бронированию",
      });

      return data;
    } catch (error: any) {
      logger.error('Error adding guest', { error });
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось добавить гостя",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateGuestData = async (guestId: string, updates: {
    full_name?: string;
    email?: string;
    phone?: string;
  }) => {
    try {
      logger.info('Updating guest data in Selectel', { guestId, updates });

      const { data, error } = await supabase.functions.invoke('selectel-proxy', {
        body: {
          operation: 'update_guest_data',
          data: {
            guest_id: guestId,
            updates
          }
        }
      });

      if (error) throw error;

      if (data.success) {
        queryClient.invalidateQueries({
          queryKey: ['optimized-bookings', currentHotel?.id],
          refetchType: 'active'
        });

        toast({
          title: "Успешно",
          description: "Данные гостя обновлены",
        });
      } else {
        throw new Error('Не удалось обновить данные в Selectel');
      }

      return data;
    } catch (error: any) {
      logger.error('Error updating guest data', { error });
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось обновить данные гостя",
        variant: "destructive",
      });
      throw error;
    }
  };

  return { addPayment, updatePayment, deletePayment, addService, addGuest, updateGuestData };
};
